/**
 * @file beam-result-reaction-summary.js
 * @description This module provides functions to summarize support reactions for load cases, combinations, and service cases in a beam design project.
 */

import { showBigPointLoad, showBigMoment, bigPointLoadUnit, bigMomentUnit, showLengthWithUnits } from "ws-units";
/**
 * Show reaction summary for load cases, combinations, and service cases.
 *
 * @param {Object} project - The BeamProject instance.
 * @param {Object} results - Dictionary of results (from Python API or calculation).
 * @returns {Object} - An object containing arrays of reactions for each type of result.
 */
export function showReactionSummary(project, results) {

  document.getElementById("load-case-reactions-table").innerHTML = makeTableForReactions(getSupportReactions(project, results, "load_case_results", project.design.loadCases));
  document.getElementById("load-combination-reactions-table").innerHTML = makeTableForReactions(getSupportReactions(project, results, "combination_results", project.design.loadCombination));
  document.getElementById("service-case-reactions-table").innerHTML = makeTableForReactions(getSupportReactions(project, results, "service_case_results", project.design.serviceCases));
}

function makeTableForReactions(reactions) {
  if (!reactions || reactions.length === 0) return "";

  // Collect all unique support locations (x values)
  const supportLocations = Array.from(
    new Set(
      reactions.flatMap(reaction => reaction.reactions.map(r => r.x.toFixed(2)))
    )
  ).sort((a, b) => Number(a) - Number(b));

  let table = "<table class='table table-borderless'>";
  table += "<thead><tr><th>Equation</th>";
  supportLocations.forEach(loc => {
    table += `<th>${showLengthWithUnits(loc)}</th>`;
  });
  table += "</tr></thead><tbody>";

  reactions.forEach(reaction => {
    table += `<tr><td>${reaction.label}</td>`;
    supportLocations.forEach(loc => {
      const r = reaction.reactions.find(rr => rr.x.toFixed(2) === loc);
      if (r) {
        table += `<td>${showBigPointLoad(r.force)} ${bigPointLoadUnit()}<br>${showBigMoment(r.moment)} ${bigMomentUnit()}</td>`;
      } else {
        table += `<td></td>`;
      }
    });
    table += "</tr>";
  });

  table += "</tbody></table>";
  return table;
}

/**
 * Show reaction summary for:
 * - load case results
 * - combination results
 * - service case results
 *
 * @param {Object} project - The BeamProject instance.
 * @param {Object} results - Dictionary of results (from Python API or calculation).
 * @param {string} resultKey - The key for the result set ("load_case_results", "combination_results", "service_case_results").
 * @param {Object} factoredLoadCases - The corresponding load case/combo/service case object from project.design.
 * @returns {void}
 */
function getSupportReactions(project, results, resultKey, factoredLoadCases) {
  if (results == null || Object.keys(results).length === 0) return;
  if (project.beam.nodes.length <= 1) return;

  // allReactions: Array of objects like
  // { label: <string>, reactions: [ { x: <number>, force: <number>, moment: <number> }, ... ] }
  let allReactions = [];

  const resultArray = results[resultKey];
  if (!Array.isArray(resultArray)) return;

  for (let i = 0; i < resultArray.length; i++) {
    const reactionResults = [];
    const resultCaseSet = resultArray[i];
    if (resultCaseSet == null || Object.keys(resultCaseSet).length === 0) continue;

    let reactions = resultCaseSet["node_reactions"];

    const eqMatch = resultCaseSet["equation_name"]?.match(/^EQ(\d+)$/);
    if (!eqMatch) continue;
    const index = Number(eqMatch[1]) - 1;
    const equation = factoredLoadCases["equations"]?.[index];
    const displayEquation = factoredLoadCases["prettyEquations"]?.[index]?.['EQ'];
    if (!equation || !displayEquation) continue;

    const loadCaseLabels = Object.keys(equation);
    
    for (let s = 0; s < reactions.length; s++) {
      const x = reactions[s]["x"];
      if (!project.beam.isNodeAtThisLocation(x)) continue;

      let force = reactions[s]["fy"];
      const pointLoadAtReaction = project.beam.getPointLoadAtLocation(x);
      if (pointLoadAtReaction && project.beam.isSupportPinnedOrFixed(x)) {
        let additionalLoad = 0;
        loadCaseLabels.forEach(loadCaseLabel => {
          const factor = Number(equation[loadCaseLabel]);
          const load = Number(pointLoadAtReaction[loadCaseLabel]);
          if (!isNaN(factor) && !isNaN(load)) {
            additionalLoad += factor * load;
          }
        });
        force += additionalLoad;
      }

      var moment = reactions[s]["mz"];

      // Only add if at least one is non-zero
      if (force !== 0 || moment !== 0) {
        reactionResults.push({ x, force, moment });
      }
    }

    if (reactionResults.length > 0) {
      allReactions.push({ label: displayEquation, reactions: reactionResults });
    }
  }

  return allReactions;
}